import { createServerFn } from "@tanstack/react-start"
import { requireAuth, requireScopes, rateLimitFunction, createPostHogTrackingMiddleware } from "~/lib/middleware"
import z from "zod"
import { LogtoScopes } from "~/lib/logto/logto"
import { ScopeRule } from "~/lib/scopes"
import { ApiKey, apiKeySchema, ApiResponse } from "~/lib/schemas"

export const deleteApiKeySchema = z.object({
  key_id: z.string().min(1, 'API key ID is required'),
})

export type DeleteApiKeyRequest = z.infer<typeof deleteApiKeySchema>

export const deleteApiKey = createServerFn({
  method: 'POST',
})
  .middleware([requireAuth, rateLimitFunction, requireScopes(ScopeRule.OR([LogtoScopes.AdminOrgKeysCrud, ScopeRule.ORG(LogtoScopes.OrgKeyCrud)])), createPostHogTrackingMiddleware('delete_api_key')])
  .validator(deleteApiKeySchema)
  .handler(async (ctx) => {
    const { key_id } = ctx.data
    
    // Use real API
    const API_MANAGEMENT_BASE_URL = process.env.API_MANAGEMENT_BASE_URL || 'http://localhost:8000'
    const BFF_API_KEY = process.env.API_MANAGEMENT_API_KEY
    
    if (!BFF_API_KEY) {
      throw new Error('API_MANAGEMENT_API_KEY environment variable is required')
    }
    //if this is a org token, then check that the orgs match
    if(ctx.context.accessToken.organization_id) {
      const response = await fetch(`${API_MANAGEMENT_BASE_URL}/api/v4/admin/organizations/keys/info?${new URLSearchParams({organization_id: ctx.context.accessToken.organization_id})}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${BFF_API_KEY}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }))
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`)
      }

      const result = await response.json() as ApiResponse<ApiKey[]>
      const apiKeys = z.array(apiKeySchema).parse(result.data ?? [])
      const apiKey = apiKeys.find(key => key.key_id === key_id)
      if(!apiKey) {
        throw new Error('You are not authorized to delete this API key')
      }

    }
    
    
    const params = new URLSearchParams({ key_id })
    
    const response = await fetch(`${API_MANAGEMENT_BASE_URL}/api/v4/admin/api-keys/delete?${params}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${BFF_API_KEY}`,
        'Content-Type': 'application/json',
      },
    })
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`)
    }
    
    return { success: true, message: 'API key deleted successfully' }
  }) 
